import React, { useState, useEffect, useCallback } from "react";
import { getAPIURL, getFormattedPrice, getVendorID } from "../../../services/helper";
import http from "../../../services/http";
import DataTable from "react-data-table-component";
import { Spinner, Container, Card, Input, Button, Badge, Nav, NavItem, NavLink, TabContent, TabPane, Alert } from "reactstrap";
import classnames from "classnames";
import { useDispatch } from "react-redux";
import { openViewWithParams } from "../../../ReduxToolkit/Reducers/viewReducer";
import FinancialSummary from "../../Registers/components/FinancialSummary";
import InvoiceModal from "../components/InvoiceModal";

const EndDayReports = ({ showLatest = false }) => {
  const api_url = getAPIURL();
  const vendor_id = getVendorID();
  const dispatch = useDispatch();
  const today = new Date().toISOString().split("T")[0];

  const [eodList, setEodList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: showLatest ? today : "",
    end: showLatest ? today : "",
  });

  // Detail state
  const [selectedEod, setSelectedEod] = useState(null);
  const [registers, setRegisters] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");
  const [combinedData, setCombinedData] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [failedRegisters, setFailedRegisters] = useState([]);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchEodList();
  }, []);

  const fetchEodList = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ vendor_id });
      if (dateRange.start) params.append("start_date", dateRange.start);
      if (dateRange.end) params.append("end_date", dateRange.end);
      const { data } = await http.get(`${api_url}/reports/end-day?${params}`);
      const list = Array.isArray(data) ? data.sort((a, b) => b.eod_id - a.eod_id) : [];
      setEodList(list);
    } catch (error) {
      console.error("Error fetching EOD list:", error);
    }
    setLoading(false);
  };

  const openEodDetail = async (eod) => {
    setSelectedEod(eod);
    setLoadingDetail(true);
    setActiveTab("summary");
    setDetailError(null);
    setFailedRegisters([]);
    setCombinedData(null);
    try {
      const { data } = await http.get(`${api_url}/reports/end-day/${eod.eod_id}/registers`);
      const regList = Array.isArray(data) ? data : [];
      setRegisters(regList);

      // Fetch full report for each register and combine
      const failed = [];
      const allReports = await Promise.all(
        regList.map(async (reg) => {
          try {
            const { data: report } = await http.get(`${api_url}/registers/report/${reg.register_id}`);
            return report;
          } catch {
            failed.push(reg.register_id);
            return null;
          }
        })
      );

      if (failed.length > 0) setFailedRegisters(failed);

      if (allReports.every((r) => r === null) && regList.length > 0) {
        setDetailError("Could not load report data for any register. Please try again.");
        setLoadingDetail(false);
        return;
      }

      const validReports = allReports.filter(Boolean);
      const combined = {
        transactions: [],
        items_report: [],
        taxes: [],
        paytypes: {},
        total_sales: 0,
        total_amount: 0,
        total_tax: 0,
        total_discount: 0,
        initial_cash: 0,
        final_cash: 0,
        refund_amount: 0,
        items_summary: { total_items_sold: 0, total_amount: 0 },
        profit: 0,
        revenue: 0,
      };

      validReports.forEach((report) => {
        const info = report.register_info || {};
        const txns = info.transactions || report.transactions || [];
        combined.transactions.push(...txns);
        combined.taxes.push(...(report.taxes || []));
        combined.total_sales += parseInt(info.total_sales || 0);
        combined.total_amount += parseFloat(info.total_amount || 0);
        combined.total_tax += parseFloat(info.total_tax || 0);
        combined.total_discount += parseFloat(info.total_discount || 0);
        combined.initial_cash += parseFloat(info.initial_cash || 0);
        combined.final_cash += parseFloat(info.final_cash || 0);
        combined.refund_amount += parseFloat(info.refund_amount || 0);

        // Combine items
        (report.items_report || []).forEach((item) => {
          const existing = combined.items_report.find((i) => i.product_id === item.product_id);
          if (existing) {
            existing.total_quantity += parseInt(item.total_quantity || 0);
            existing.total_amount += parseFloat(item.total_amount || 0);
            existing.orders_count += parseInt(item.orders_count || 0);
          } else {
            combined.items_report.push({ ...item, total_quantity: parseInt(item.total_quantity || 0), total_amount: parseFloat(item.total_amount || 0), orders_count: parseInt(item.orders_count || 0) });
          }
        });

        // Combine pay types
        const breakdown = info.split_payment_summary?.payment_type_breakdown || {};
        Object.entries(breakdown).forEach(([type, d]) => {
          if (!combined.paytypes[type]) combined.paytypes[type] = { total: 0, net_total: 0, change_given: 0, count: 0 };
          combined.paytypes[type].total += parseFloat(d.total || 0);
          combined.paytypes[type].net_total += parseFloat(d.net_total || 0);
          combined.paytypes[type].change_given += parseFloat(d.change_given || 0);
          combined.paytypes[type].count += parseInt(d.count || 0);
        });

        const iSummary = report.items_summary || {};
        combined.items_summary.total_items_sold += parseInt(iSummary.total_items_sold || 0);
        combined.items_summary.total_amount += parseFloat(iSummary.total_amount || 0);

        const profitData = report.profit_data || {};
        combined.profit += parseFloat(profitData.profit || 0);
        combined.revenue += parseFloat(profitData.revenue || 0);
      });

      setCombinedData(combined);
    } catch (error) {
      console.error("Error fetching EOD registers:", error);
      setDetailError("Failed to load EOD details. Please try again.");
    }
    setLoadingDetail(false);
  };

  const handleTransactionClick = async (row) => {
    try {
      const { data } = await http.get(`${api_url}/reports/transactions/${row.order_id}`);
      setSelectedOrder(data);
      setInvoiceModalOpen(true);
    } catch (error) {
      console.error("Error fetching transaction:", error);
    }
  };

  const transactionsColumns = [
    { name: "Order ID", selector: (row) => row.order_id, sortable: true },
    { name: "Order Date", selector: (row) => row.order_date, sortable: true },
    { name: "Tax", selector: (row) => getFormattedPrice(row.tax_collected || row.tax_amount || 0), sortable: true },
    { name: "Discount", selector: (row) => getFormattedPrice(row.discount || row.discount_amount || 0), sortable: true },
    { name: "Total", selector: (row) => getFormattedPrice(row.order_total), sortable: true },
    { name: "Refund", cell: (row) => { const r = parseFloat(row.refund_amount || 0); return <span style={{ color: r > 0 ? "red" : "inherit" }}>{r > 0 ? getFormattedPrice(r) : "-"}</span>; }, sortable: true },
  ];

  const itemsColumns = [
    { name: "Product", selector: (row) => row.product_name, sortable: true },
    { name: "Unit Price", selector: (row) => getFormattedPrice(row.unit_price), sortable: true },
    { name: "Quantity", selector: (row) => row.total_quantity, sortable: true },
    { name: "Total", selector: (row) => getFormattedPrice(row.total_amount), sortable: true },
    { name: "Orders", selector: (row) => row.orders_count, sortable: true },
  ];

  const taxesColumns = [
    { name: "Order ID", selector: (row) => row.order_id, sortable: true },
    { name: "Tax", selector: (row) => getFormattedPrice(row.tax_collected || row.tax_amount), sortable: true },
    { name: "Order Total", selector: (row) => getFormattedPrice(row.order_total), sortable: true },
  ];

  const paytypesColumns = [
    { name: "Payment Type", selector: (row) => row.payment_type, sortable: true },
    { name: "Collected", selector: (row) => getFormattedPrice(row.total_amount), sortable: true },
    { name: "Change", selector: (row) => getFormattedPrice(row.change_given), sortable: true },
    { name: "Net", selector: (row) => getFormattedPrice(row.net_total), sortable: true },
    { name: "Count", selector: (row) => row.count, sortable: true },
  ];

  const registerColumns = [
    { name: "Reg ID", selector: (row) => row.register_id, sortable: true },
    { name: "Cashier", selector: (row) => row.cashier, sortable: true },
    { name: "Sales", selector: (row) => row.total_sales, sortable: true },
    { name: "Amount", selector: (row) => getFormattedPrice(row.total_amount), sortable: true },
    { name: "Tax", selector: (row) => getFormattedPrice(row.total_tax), sortable: true },
    { name: "Refund", cell: (row) => { const r = parseFloat(row.refund_amount || 0); return <span style={{ color: r > 0 ? "red" : "inherit" }}>{r > 0 ? getFormattedPrice(r) : "-"}</span>; }, sortable: true },
  ];

  // Detail view
  if (selectedEod) {
    const paytypesData = combinedData ? Object.entries(combinedData.paytypes).map(([type, d]) => ({
      payment_type: type, total_amount: d.total, net_total: d.net_total, change_given: d.change_given, count: d.count,
    })) : [];

    const combinedRegisterInfo = combinedData ? {
      id: `EOD #${selectedEod.eod_id}`,
      title: `End of Day - ${selectedEod.eod_date}`,
      cashier: `${registers.length} register(s)`,
      initial_cash: combinedData.initial_cash,
      final_cash: combinedData.final_cash,
      total_sales: combinedData.total_sales,
      total_amount: combinedData.total_amount,
      total_discount: combinedData.total_discount,
      total_tax: combinedData.total_tax,
      refund_amount: combinedData.refund_amount,
    } : {};

    return (
      <div className="page-body">
        <Container className="mt-2">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button color="secondary" size="sm" onClick={() => { setSelectedEod(null); setCombinedData(null); setDetailError(null); setFailedRegisters([]); }}>
              ← Back to EOD List
            </Button>
            <h5 className="mb-0">
              EOD #{selectedEod.eod_id} — {selectedEod.eod_date}
              <Badge color={selectedEod.status === "locked" ? "success" : "warning"} className="ms-2" style={{ fontSize: "11px" }}>
                {selectedEod.status}
              </Badge>
            </h5>
            <div></div>
          </div>

          {loadingDetail ? (
            <div className="text-center py-5">
              <Spinner color="primary" size="lg" />
              <p className="mt-2">Loading EOD Details...</p>
            </div>
          ) : detailError ? (
            <div className="text-center py-5">
              <p className="text-danger mb-3">{detailError}</p>
              <Button color="primary" onClick={() => openEodDetail(selectedEod)}>Retry</Button>
            </div>
          ) : combinedData ? (
            <>
              {failedRegisters.length > 0 && (
                <Alert color="warning" className="mb-3">
                  <strong>Partial data:</strong> Register(s) {failedRegisters.join(", ")} failed to load. Totals shown below may be incomplete.
                </Alert>
              )}
              <Nav tabs className="mb-3">
                {["summary", "items", "taxes", "transactions", "paytypes", "registers", "profit"].map((tab) => (
                  <NavItem key={tab}>
                    <NavLink
                      className={classnames({ active: activeTab === tab })}
                      onClick={() => setActiveTab(tab)}
                      style={{ cursor: "pointer" }}
                    >
                      {tab === "summary" && "Summary"}
                      {tab === "items" && `Items (${combinedData.items_summary.total_items_sold})`}
                      {tab === "taxes" && `Taxes (${combinedData.taxes.length})`}
                      {tab === "transactions" && `Transactions (${combinedData.transactions.length})`}
                      {tab === "paytypes" && `Pay Types (${paytypesData.length})`}
                      {tab === "registers" && `Registers (${registers.length})`}
                      {tab === "profit" && "Profit"}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="summary">
                  <FinancialSummary registerInfo={combinedRegisterInfo} />
                </TabPane>

                <TabPane tabId="items">
                  <DataTable columns={itemsColumns} data={combinedData.items_report} pagination striped highlightOnHover />
                  <div className="text-center mt-3 p-3 bg-dark text-white rounded">
                    <strong>Total Items: {combinedData.items_summary.total_items_sold} | Total: {getFormattedPrice(combinedData.items_summary.total_amount)}</strong>
                  </div>
                </TabPane>

                <TabPane tabId="taxes">
                  <DataTable columns={taxesColumns} data={combinedData.taxes} pagination striped highlightOnHover pointerOnHover onRowClicked={handleTransactionClick} />
                  <div className="text-center mt-3 p-3 bg-dark text-white rounded">
                    <strong>Total Tax: {getFormattedPrice(combinedData.total_tax)}</strong>
                  </div>
                </TabPane>

                <TabPane tabId="transactions">
                  <DataTable columns={transactionsColumns} data={combinedData.transactions} pagination striped highlightOnHover pointerOnHover onRowClicked={handleTransactionClick} />
                  <div className="text-center mt-3 p-3 bg-dark text-white rounded">
                    <strong>
                      Total: {getFormattedPrice(combinedData.total_amount)}
                      {combinedData.refund_amount > 0 && (
                        <span style={{ color: "#ff6b6b" }}> | Refund: {getFormattedPrice(combinedData.refund_amount)}</span>
                      )}
                    </strong>
                  </div>
                </TabPane>

                <TabPane tabId="paytypes">
                  <DataTable columns={paytypesColumns} data={paytypesData} pagination striped highlightOnHover />
                </TabPane>

                <TabPane tabId="registers">
                  <DataTable
                    columns={registerColumns}
                    data={registers}
                    striped
                    highlightOnHover
                    pointerOnHover
                    onRowClicked={(row) =>
                      dispatch(openViewWithParams({
                        view: "close_register_summary",
                        params: { register_id: row.register_id, backTo: "reports_eod" },
                      }))
                    }
                  />
                </TabPane>

                <TabPane tabId="profit">
                  <div className="row">
                    <div className="col-md-4">
                      <div className="card">
                        <div className="card-body text-center">
                          <h5 className="card-title">Revenue</h5>
                          <h3 className="text-primary">{getFormattedPrice(combinedData.revenue)}</h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card">
                        <div className="card-body text-center">
                          <h5 className="card-title">Profit</h5>
                          <h3 className="text-success">{getFormattedPrice(combinedData.profit || 0)}</h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card">
                        <div className="card-body text-center">
                          <h5 className="card-title">Profit Margin</h5>
                          <h3 className="text-info">
                            {combinedData.revenue > 0 && combinedData.profit
                              ? `${((combinedData.profit / combinedData.revenue) * 100).toFixed(1)}%`
                              : "0%"}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabPane>
              </TabContent>
            </>
          ) : null}

          <InvoiceModal
            isOpen={invoiceModalOpen}
            toggle={() => setInvoiceModalOpen(false)}
            selectedOrder={selectedOrder}
            onPrintReceipt={() => {}}
            onPrintLabel={() => {}}
            onRefund={() => {}}
            onDelete={() => {}}
          />
        </Container>
      </div>
    );
  }

  const eodColumns = [
    { name: "EOD ID", selector: (row) => row.eod_id, sortable: true },
    { name: "Date", selector: (row) => row.eod_date, sortable: true },
    { name: "Registers", selector: (row) => row.total_registers, sortable: true },
    { name: "Sales", selector: (row) => row.total_sales, sortable: true },
    { name: "Amount", selector: (row) => getFormattedPrice(row.total_amount), sortable: true },
    { name: "Status", cell: (row) => <span style={{ color: row.status === "locked" ? "#28a745" : "#ffc107", fontWeight: 600 }}>{row.status}</span> },
  ];

  // List view
  return (
    <div className="page-body">
      <Container className="mt-2">
        <h4 className="text-center mb-3">End of Day Reports</h4>

        <div className="d-flex mb-3 gap-2">
          <Input type="date" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} />
          <Input type="date" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} />
          <Button color="primary" onClick={fetchEodList}>Filter</Button>
          <Button color="info" onClick={() => { setDateRange({ start: "", end: "" }); fetchEodList(); }}>Reset</Button>
        </div>

        {loading ? (
          <div className="text-center">
            <Spinner color="primary" size="lg" />
            <p>Loading EOD Records...</p>
          </div>
        ) : (
          <Card className="p-3 shadow-sm">
            <DataTable
              columns={eodColumns}
              data={eodList}
              pagination
              striped
              highlightOnHover
              pointerOnHover
              onRowClicked={openEodDetail}
              noDataComponent="No EOD records found"
            />
          </Card>
        )}
      </Container>
    </div>
  );
};

export default EndDayReports;
